// Public support-link redemption page (Mitchell 2026-04-25).
//
// Calendly-shaped UX: visitor lands on /s/<token>, sees a small card that
// names the kind of room (text/voice/video) and the link's label, picks a
// display name, hits "Join" — backend mints a guest cookie + auto-creates
// the channel, and we redirect them into the Discord shell on the right
// channel.
//
// Mounted by app.ts BEFORE auth: any path matching /s/:token (or
// /people/s/:token behind hub-core) routes here and short-circuits the
// shell. Signed-in members hitting the same URL get the same UX — they
// just don't get a guest cookie issued.
//
// Anti-disclosure: any non-redeemable state (revoked / expired / unknown)
// returns a 404 from the backend. We render a generic "not available"
// message — never leak which state.

import { API_BASE } from '../api';

interface PublicInfo {
  kind: 'text' | 'voice' | 'video';
  label: string;
  status: 'unused' | 'used';
}

interface RedeemResp {
  token: string;
  channel_id: string;
  server_id: string;
  kind: 'text' | 'voice' | 'video';
  label: string;
  guest_token: string | null;
  redirect_url: string;
}

interface ApiResponse<T> { status: 'ok' | 'error'; data?: T; error?: string; }

async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<T>;
  if (json.status === 'error') throw new Error(json.error || 'request failed');
  return json.data as T;
}

async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<T>;
  if (json.status === 'error') throw new Error(json.error || 'request failed');
  return json.data as T;
}

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

function tokenFromPath(): string | null {
  const m = window.location.pathname.match(/\/s\/([^/?#]+)/);
  return m && m[1] ? m[1] : null;
}

function injectStyles(): void {
  if (document.getElementById('sl-styles')) return;
  const style = document.createElement('style');
  style.id = 'sl-styles';
  style.textContent = `
    .sl-page { min-height: 100dvh; background: var(--surface-0, #1e1f22); color: var(--text-primary, #f2f3f5); display: flex; align-items: center; justify-content: center; padding: 40px 20px; font-family: system-ui, -apple-system, Segoe UI, sans-serif; box-sizing: border-box; }
    .sl-card { background: var(--surface-1, #2b2d31); border: 1px solid var(--border-subtle, #3f4147); border-radius: var(--radius-lg, 12px); padding: 32px; max-width: 420px; width: 100%; }
    .sl-card h1 { font-size: 22px; font-weight: 600; margin: 0 0 12px; }
    .sl-label { color: var(--text-secondary, #b5bac1); font-size: 14px; margin: 0 0 20px; line-height: 1.5; }
    .sl-field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; font-size: 13px; color: var(--text-secondary, #b5bac1); }
    .sl-field input { background: var(--surface-2, #1e1f22); border: 1px solid var(--border-subtle, #3f4147); border-radius: var(--radius-sm, 6px); color: var(--text-primary, #f2f3f5); font-size: 15px; padding: 10px 12px; outline: none; }
    .sl-field input:focus { border-color: var(--accent, #5865f2); }
    .sl-btn-primary { display: block; width: 100%; background: var(--accent, #5865f2); color: var(--text-inverse, #fff); border: none; border-radius: var(--radius-sm, 6px); font-size: 15px; font-weight: 600; padding: 12px; cursor: pointer; }
    .sl-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
    .sl-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
    .sl-error { margin-top: 12px; color: var(--danger, #f23f42); font-size: 13px; }
    .sl-empty { text-align: center; }
    .sl-empty p { color: var(--text-secondary, #b5bac1); font-size: 14px; line-height: 1.5; margin: 0; }
  `;
  document.head.appendChild(style);
}

function shell(title: string): HTMLElement {
  document.title = title;
  injectStyles();
  const root = (document.getElementById('app') ?? document.body) as HTMLElement;
  root.innerHTML = '';
  const page = document.createElement('div');
  page.className = 'sl-page';
  root.appendChild(page);
  return page;
}

function kindLabel(kind: string): string {
  if (kind === 'text') return 'Text chat';
  if (kind === 'voice') return 'Voice call';
  if (kind === 'video') return 'Video call';
  return 'Support session';
}

function renderNotFound(page: HTMLElement): void {
  page.innerHTML = `
    <div class="sl-card sl-empty">
      <h1>Link not available</h1>
      <p>This support link isn't active. Ask the person who shared it
         with you for a new one.</p>
    </div>
  `;
}

function renderInfo(page: HTMLElement, token: string, info: PublicInfo): void {
  page.innerHTML = `
    <div class="sl-card">
      <h1>Join ${esc(kindLabel(info.kind))}</h1>
      ${info.label ? `<p class="sl-label">${esc(info.label)}</p>` : ''}
      <label class="sl-field">
        <span>Display name</span>
        <input id="sl-name" type="text" maxlength="80" placeholder="Your name" />
      </label>
      <button id="sl-join" class="sl-btn-primary">Join</button>
      <div id="sl-error" class="sl-error" hidden></div>
    </div>
  `;

  const input = page.querySelector<HTMLInputElement>('#sl-name');
  const button = page.querySelector<HTMLButtonElement>('#sl-join');
  const errorEl = page.querySelector<HTMLElement>('#sl-error');
  if (!input || !button || !errorEl) return;

  input.focus();

  const submit = async (): Promise<void> => {
    button.disabled = true;
    errorEl.hidden = true;
    errorEl.textContent = '';
    try {
      const resp = await apiPost<RedeemResp>(
        `/api/support-link/${encodeURIComponent(token)}/redeem`,
        { display_name: input.value.trim() || 'Guest' }
      );
      // Hand off to the Discord shell at the resolved channel. The shell
      // honours the hub_session cookie (signed-in members) or the freshly-
      // minted hub_guest_token cookie (walk-up guests) for the narrow
      // /api/messages allowlist.
      window.location.href = resp.redirect_url;
    } catch (e) {
      errorEl.textContent = String((e as Error).message || e);
      errorEl.hidden = false;
      button.disabled = false;
    }
  };

  button.addEventListener('click', submit);
  input.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') submit();
  });
}

export async function renderSupportLink(): Promise<void> {
  const token = tokenFromPath();
  const page = shell('Join support session');
  if (!token) {
    renderNotFound(page);
    return;
  }
  try {
    const info = await apiGet<PublicInfo>(
      `/api/support-link/${encodeURIComponent(token)}/info`
    );
    renderInfo(page, token, info);
  } catch {
    // Anti-disclosure: any failure renders the generic "not available"
    // state regardless of underlying reason.
    renderNotFound(page);
  }
}

/** Returns true if the current path is a public support-link page and we
 *  should mount the redeem UI BEFORE auth. Call from app.ts init(). */
export function isPublicSupportLinkPath(): boolean {
  return /\/s\/[^/]+/.test(window.location.pathname);
}
